import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useParams } from "react-router-dom";
import {
  Extension as ExtensionIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc,
  getDoc,
  deleteDoc 
} from "firebase/firestore";
import { Firestore } from "../FirebaseAuth/firebase";
import { 
  extensionCategories
} from "../../utils/constants";
import {
  getLocalStorage,
  LOCAL_STORAGE_CURRENT_ACCOUNT,
  LOCAL_STORAGE_CURRENT_ORG,
} from "../../utils/localStorage";
import { AuthContext } from "../FirebaseAuth";
import { isAdmin, isOrgAdmin } from "../../utils";

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  extensionCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[4],
      transform: "translateY(-2px)",
    },
  },
  extensionContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  extensionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  extensionIcon: {
    marginRight: theme.spacing(1),
    width: 40,
    height: 40,
  },
  extensionMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  extensionActions: {
    marginTop: "auto",
    padding: theme.spacing(1),
  },
  priceChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  freeChip: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[800],
  },
  configField: {
    marginBottom: theme.spacing(2),
  },
  uninstallButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
}));

const ExtensionMarketplace = ({ siteData }) => {
  const classes = useStyles();
  const params = useParams();
  const { authUser } = useContext(AuthContext);
  const [extensions, setExtensions] = useState([]);
  const [filteredExtensions, setFilteredExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [configData, setConfigData] = useState({});
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptions, setSubscriptions] = useState({});
  const [userData, setUserData] = useState(null);
  const [uninstallDialog, setUninstallDialog] = useState({ open: false, extension: null, subscription: null });
  const [uninstalling, setUninstalling] = useState(false);

  const currentAccount = getLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT);
  const currentOrganization = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);


  const fetchExtensions = useCallback(async () => {
    try {
      setLoading(true);
      const servicesRef = collection(Firestore, "services");
      const extensionsQuery = query(
        servicesRef, 
        where("type", "==", "extension"),
        where("active", "==", true)
      );
      
      const snapshot = await getDocs(extensionsQuery);
      const extensionsList = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        // Inline visibility check to avoid dependency issues
        let isVisible = true;
        
        // Check account type restrictions
        if (data.accountTypes && data.accountTypes.length > 0) {
          if (!data.accountTypes.includes("all") && 
              !data.accountTypes.includes(currentAccount?.accountType?.toLowerCase())) {
            isVisible = false;
          }
        }

        // Check organization restrictions
        if (isVisible && data.excludeOrganizations && 
            data.excludeOrganizations.includes(currentOrganization?.id)) {
          isVisible = false;
        }

        if (isVisible && data.showOrganizations && data.showOrganizations.length > 0) {
          if (!data.showOrganizations.includes(currentOrganization?.id)) {
            isVisible = false;
          }
        }

        // Check account restrictions
        if (isVisible && data.excludeAccounts && 
            data.excludeAccounts.includes(currentAccount?.id)) {
          isVisible = false;
        }

        if (isVisible && data.showAccounts && data.showAccounts.length > 0) {
          if (!data.showAccounts.includes(currentAccount?.id)) {
            isVisible = false;
          }
        }

        // Check platform compatibility
        if (isVisible && data.platforms && data.platforms.length > 0 && 
            !data.platforms.includes("all") && siteData?.siteType) {
          const sitePlatform = siteData.siteType.toLowerCase();
          const supportedPlatforms = data.platforms.map(p => p.toLowerCase());
          if (!supportedPlatforms.includes(sitePlatform)) {
            isVisible = false;
          }
        }
        
        if (isVisible) {
          extensionsList.push({ id: doc.id, ...data });
        }
      });
      
      setExtensions(extensionsList);
    } catch (error) {
      console.error("Error fetching extensions:", error);
    } finally {
      setLoading(false);
    }
  }, [currentAccount?.id, currentOrganization?.id, params.siteId, siteData?.siteType]);


  // Direct function instead of useCallback to avoid dependency issues
  const filterExtensions = () => {
    let filtered = extensions;
    
    if (categoryFilter !== "all") {
      filtered = filtered.filter(ext => ext.category === categoryFilter);
    }
    
    setFilteredExtensions(filtered);
  };

  const handleExtensionClick = (extension) => {
    setSelectedExtension(extension);
    setConfigData({});
    setDialogOpen(true);
  };

  const handleSubscribe = async (scope = "site") => {
    if (!selectedExtension) return;

    try {
      setSubscribing(true);
      
      // Determine the collection path based on scope
      let collectionPath;
      switch (scope) {
        case "organization":
          collectionPath = `organizations/${currentOrganization.id}/extensionSubscriptions`;
          break;
        case "account":
          collectionPath = `accounts/${currentAccount.id}/extensionSubscriptions`;
          break;
        default:
          collectionPath = `accounts/${currentAccount.id}/sites/${params.siteId}/extensionSubscriptions`;
      }

      const subscriptionData = {
        extensionId: selectedExtension.id,
        scope,
        subscribedAt: new Date(),
        subscribedBy: currentAccount.id,
        status: "active",
        configuration: configData,
        lastExecution: null,
        nextExecution: null,
        executionCount: 0,
      };

      // Add Stripe subscription info if it's a paid extension
      if (selectedExtension.stripePriceId) {
        // This would integrate with Stripe subscription creation
        // For now, we'll mark it as pending until payment is processed
        subscriptionData.status = "pending_payment";
        subscriptionData.stripePriceId = selectedExtension.stripePriceId;
        subscriptionData.stripeProductId = selectedExtension.stripeProductId;
      }

      const subscriptionRef = doc(Firestore, collectionPath, selectedExtension.id);
      await setDoc(subscriptionRef, subscriptionData);

      // Log the installation activity
      await logExtensionActivity(selectedExtension.id, "install", scope, subscriptionData);
      
      // Refresh subscriptions
      const fetchSubs = async () => {
        if (!params.siteId || !currentAccount?.id) return;
        
        try {
          const siteSubsRef = collection(Firestore, `accounts/${currentAccount.id}/sites/${params.siteId}/extensionSubscriptions`);
          const accountSubsRef = collection(Firestore, `accounts/${currentAccount.id}/extensionSubscriptions`);
          const orgSubsRef = currentOrganization?.id ? 
            collection(Firestore, `organizations/${currentOrganization.id}/extensionSubscriptions`) : null;

          const promises = [getDocs(siteSubsRef), getDocs(accountSubsRef)];
          if (orgSubsRef) promises.push(getDocs(orgSubsRef));

          const results = await Promise.all(promises);
          const [siteSnap, accountSnap, orgSnap] = results;

          const subs = {};
          siteSnap.forEach(doc => {
            subs[doc.id] = { ...doc.data(), scope: "site", docId: doc.id };
          });
          accountSnap.forEach(doc => {
            if (!subs[doc.id]) {
              subs[doc.id] = { ...doc.data(), scope: "account", docId: doc.id };
            }
          });
          if (orgSnap) {
            orgSnap.forEach(doc => {
              if (!subs[doc.id]) {
                subs[doc.id] = { ...doc.data(), scope: "organization", docId: doc.id };
              }
            });
          }
          setSubscriptions(subs);
        } catch (error) {
          console.error("Error fetching subscriptions:", error);
        }
      };
      
      await fetchSubs();
      setDialogOpen(false);
      setSelectedExtension(null);
    } catch (error) {
      console.error("Error subscribing to extension:", error);
    } finally {
      setSubscribing(false);
    }
  };

  const handleUninstallClick = (extension, subscription) => {
    setUninstallDialog({ 
      open: true, 
      extension, 
      subscription 
    });
  };

  const handleUninstall = async () => {
    if (!uninstallDialog.extension || !uninstallDialog.subscription) return;

    try {
      setUninstalling(true);
      
      const { extension, subscription } = uninstallDialog;
      
      // Determine the collection path based on scope
      let collectionPath;
      switch (subscription.scope) {
        case "organization":
          collectionPath = `organizations/${currentOrganization.id}/extensionSubscriptions`;
          break;
        case "account":
          collectionPath = `accounts/${currentAccount.id}/extensionSubscriptions`;
          break;
        default:
          collectionPath = `accounts/${currentAccount.id}/sites/${params.siteId}/extensionSubscriptions`;
      }

      const subscriptionRef = doc(Firestore, collectionPath, extension.id);
      await deleteDoc(subscriptionRef);

      // Log the uninstallation activity
      await logExtensionActivity(extension.id, "uninstall", subscription.scope, subscription);
      
      // Refresh subscriptions
      const fetchSubs = async () => {
        if (!params.siteId || !currentAccount?.id) return;
        
        try {
          const siteSubsRef = collection(Firestore, `accounts/${currentAccount.id}/sites/${params.siteId}/extensionSubscriptions`);
          const accountSubsRef = collection(Firestore, `accounts/${currentAccount.id}/extensionSubscriptions`);
          const orgSubsRef = currentOrganization?.id ? 
            collection(Firestore, `organizations/${currentOrganization.id}/extensionSubscriptions`) : null;

          const promises = [getDocs(siteSubsRef), getDocs(accountSubsRef)];
          if (orgSubsRef) promises.push(getDocs(orgSubsRef));

          const results = await Promise.all(promises);
          const [siteSnap, accountSnap, orgSnap] = results;

          const subs = {};
          siteSnap.forEach(doc => {
            subs[doc.id] = { ...doc.data(), scope: "site", docId: doc.id };
          });
          accountSnap.forEach(doc => {
            if (!subs[doc.id]) {
              subs[doc.id] = { ...doc.data(), scope: "account", docId: doc.id };
            }
          });
          if (orgSnap) {
            orgSnap.forEach(doc => {
              if (!subs[doc.id]) {
                subs[doc.id] = { ...doc.data(), scope: "organization", docId: doc.id };
              }
            });
          }
          setSubscriptions(subs);
        } catch (error) {
          console.error("Error fetching subscriptions:", error);
        }
      };
      
      await fetchSubs();
      setUninstallDialog({ open: false, extension: null, subscription: null });
    } catch (error) {
      console.error("Error uninstalling extension:", error);
    } finally {
      setUninstalling(false);
    }
  };

  const logExtensionActivity = async (extensionId, action, scope, data) => {
    try {
      // Use params.siteId instead of siteData?.id to ensure we have the correct siteId
      const siteId = params.siteId || siteData?.id;
      const accountId = params.accountId || currentAccount?.id;
      
      if (!siteId || !accountId) {
        console.warn("Cannot log extension activity: missing siteId or accountId", { siteId, accountId });
        return;
      }

      const activityData = {
        action: action,
        extensionId: extensionId,
        scope: scope,
        timestamp: new Date(),
        userId: authUser?.user?.uid,
        accountId: accountId,
        siteId: siteId,
        details: {
          extensionName: action === "install" ? selectedExtension?.name : uninstallDialog?.extension?.name,
          configuration: data.configuration || {},
          status: data.status || "completed"
        }
      };

      const activityRef = doc(
        collection(Firestore, `accounts/${accountId}/sites/${siteId}/activities`)
      );
      await setDoc(activityRef, activityData);
    } catch (error) {
      console.error("Error logging extension activity:", error);
    }
  };

  const isSubscribed = (extensionId) => {
    return !!subscriptions[extensionId];
  };

  const getSubscriptionInfo = (extensionId) => {
    return subscriptions[extensionId];
  };

  // Permission checks
  const canInstallForAccount = () => {
    return userData && isAdmin(userData.role);
  };

  const canInstallForOrganization = () => {
    return userData && isOrgAdmin(userData.role) && currentOrganization;
  };

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (authUser?.user?.uid) {
        try {
          const userDocRef = doc(Firestore, "users", authUser.user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };

    fetchUserData();
  }, [authUser?.user?.uid]);

  useEffect(() => {
    const fetchSubs = async () => {
      if (!params.siteId || !currentAccount?.id) return;
      
      try {
        // Check for existing subscriptions at site, account, and organization levels
        const siteSubsRef = collection(Firestore, `accounts/${currentAccount.id}/sites/${params.siteId}/extensionSubscriptions`);
        const accountSubsRef = collection(Firestore, `accounts/${currentAccount.id}/extensionSubscriptions`);
        const orgSubsRef = currentOrganization?.id ? 
          collection(Firestore, `organizations/${currentOrganization.id}/extensionSubscriptions`) : null;

        const promises = [
          getDocs(siteSubsRef),
          getDocs(accountSubsRef)
        ];
        
        if (orgSubsRef) {
          promises.push(getDocs(orgSubsRef));
        }

        const results = await Promise.all(promises);
        const [siteSnap, accountSnap, orgSnap] = results;

        const subs = {};
        
        // Site-level subscriptions
        siteSnap.forEach(doc => {
          subs[doc.id] = { ...doc.data(), scope: "site", docId: doc.id };
        });

        // Account-level subscriptions
        accountSnap.forEach(doc => {
          if (!subs[doc.id]) {
            subs[doc.id] = { ...doc.data(), scope: "account", docId: doc.id };
          }
        });

        // Organization-level subscriptions
        if (orgSnap) {
          orgSnap.forEach(doc => {
            if (!subs[doc.id]) {
              subs[doc.id] = { ...doc.data(), scope: "organization", docId: doc.id };
            }
          });
        }

        setSubscriptions(subs);
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
      }
    };
    
    fetchSubs();
  }, [params.siteId, currentAccount?.id, currentOrganization?.id]);

  useEffect(() => {
    filterExtensions();
  }, [extensions, categoryFilter]);

  const renderExtensionCard = (extension) => {
    const subscription = getSubscriptionInfo(extension.id);
    const subscribed = isSubscribed(extension.id);

    return (
      <Grid item xs={12} sm={6} md={4} key={extension.id}>
        <Card 
          className={classes.extensionCard}
          onClick={() => handleExtensionClick(extension)}
        >
          <CardContent className={classes.extensionContent}>
            <Box className={classes.extensionHeader}>
              <Avatar 
                src={extension.imageUrl} 
                className={classes.extensionIcon}
              >
                <ExtensionIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" component="h3">
                  {extension.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  v{extension.version}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" color="textSecondary" paragraph>
              {extension.description}
            </Typography>
            
            <Box className={classes.extensionMeta}>
              <Chip 
                size="small" 
                label={extension.category}
                color="primary"
                variant="outlined"
              />
              
              <Chip
                size="small"
                icon={<ScheduleIcon />}
                label={extension.frequency}
                variant="outlined"
              />
              
              {extension.stripePriceId ? (
                <Chip
                  size="small"
                  icon={<MoneyIcon />}
                  label="Paid"
                  className={classes.priceChip}
                />
              ) : (
                <Chip
                  size="small"
                  label="Free"
                  className={classes.freeChip}
                />
              )}
            </Box>
          </CardContent>
          
          <Box className={classes.extensionActions}>
            {subscribed ? (
              <Box display="flex" gap={1}>
                <Button 
                  variant="outlined" 
                  color="success" 
                  fullWidth
                  disabled
                >
                  Installed ({subscription?.scope})
                </Button>
                <Button 
                  variant="outlined" 
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUninstallClick(extension, subscription);
                  }}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </Box>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth
              >
                {extension.stripePriceId ? "Subscribe" : "Install"}
              </Button>
            )}
          </Box>
        </Card>
      </Grid>
    );
  };

  const renderConfigDialog = () => (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configure {selectedExtension?.name}
      </DialogTitle>
      <DialogContent>
        {selectedExtension?.configurationFields && selectedExtension.configurationFields.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This extension requires configuration before activation.
            </Alert>
            {selectedExtension.configurationFields.map((field, index) => (
              <TextField
                key={index}
                fullWidth
                label={field.label}
                type={field.type || "text"}
                required={field.required}
                value={configData[field.name] || ""}
                onChange={(e) => setConfigData({
                  ...configData,
                  [field.name]: e.target.value
                })}
                className={classes.configField}
                helperText={field.description}
              />
            ))}
          </>
        ) : (
          <Alert severity="success">
            This extension requires no additional configuration.
          </Alert>
        )}
        
        {selectedExtension?.stripePriceId && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This is a paid extension. You will be redirected to complete payment after configuration.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>
          Cancel
        </Button>
        <Button 
          onClick={() => handleSubscribe("site")}
          variant="contained"
          disabled={subscribing}
        >
          {subscribing && <CircularProgress size={20} sx={{ mr: 1 }} />}
          {selectedExtension?.stripePriceId ? "Subscribe" : "Install"} for Site
        </Button>
        {canInstallForAccount() && (
          <Button 
            onClick={() => handleSubscribe("account")}
            variant="outlined"
            disabled={subscribing}
          >
            Install for Collection
          </Button>
        )}
        {canInstallForOrganization() && (
          <Button 
            onClick={() => handleSubscribe("organization")}
            variant="outlined"
            disabled={subscribing}
          >
            Install for Organization
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter Controls */}
      <Box className={classes.filterContainer}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Filter by Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {extensionCategories.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Extensions Grid */}
      {filteredExtensions.length > 0 ? (
        <Grid container spacing={3}>
          {filteredExtensions.map(renderExtensionCard)}
        </Grid>
      ) : (
        <Alert severity="info">
          No extensions available for your current setup.
        </Alert>
      )}

      {/* Configuration Dialog */}
      {renderConfigDialog()}
      
      {/* Uninstall Confirmation Dialog */}
      <Dialog 
        open={uninstallDialog.open} 
        onClose={() => setUninstallDialog({ open: false, extension: null, subscription: null })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm Uninstall
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to uninstall <strong>{uninstallDialog.extension?.name}</strong>?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This will remove the extension from your {uninstallDialog.subscription?.scope} and stop all automation workflows. 
            This action cannot be undone.
          </Typography>
          {uninstallDialog.extension?.stripePriceId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Note: You may need to cancel your subscription separately in your billing settings.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUninstallDialog({ open: false, extension: null, subscription: null })}
            disabled={uninstalling}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUninstall}
            variant="contained"
            color="error"
            disabled={uninstalling}
            startIcon={uninstalling ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {uninstalling ? 'Uninstalling...' : 'Uninstall'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExtensionMarketplace;